import type { AccountPlatform, ContentType, ContentQueueItem } from './types'

export interface PlatformConfig {
  id: AccountPlatform
  displayName: string
  charLimit: number
  supportsMedia: boolean
  mediaRequired: boolean
  supportedContentTypes: ContentType[]
  postUrlTemplate: string | null
  replyUrlTemplate: string | null
  oauthSupported: boolean
  requiresHandle: boolean
  connectPermissions: string[]
  enabled: boolean
}

export const PLATFORM_CONFIGS: Record<AccountPlatform, PlatformConfig> = {
  twitter: {
    id: 'twitter',
    displayName: 'X',
    charLimit: 280,
    supportsMedia: true,
    mediaRequired: false,
    supportedContentTypes: ['single_tweet', 'thread', 'quote_reply'],
    postUrlTemplate: 'https://x.com/i/web/status/{id}',
    replyUrlTemplate: 'https://x.com/i/web/status/{id}',
    oauthSupported: true,
    requiresHandle: false,
    connectPermissions: [
      'Read your posts',
      'Post on your behalf',
      'Read your profile',
    ],
    enabled: true,
  },
  instagram: {
    id: 'instagram',
    displayName: 'Instagram',
    charLimit: 2200,
    supportsMedia: true,
    mediaRequired: true,
    supportedContentTypes: ['instagram_caption'],
    postUrlTemplate: 'https://www.instagram.com/p/{id}/',
    replyUrlTemplate: null,
    oauthSupported: true,
    requiresHandle: false,
    connectPermissions: [
      'Access your Instagram profile',
      'Publish content on your behalf',
      'Read your media and insights',
    ],
    enabled: false,
  },
  threads: {
    id: 'threads',
    displayName: 'Threads',
    charLimit: 500,
    supportsMedia: true,
    mediaRequired: false,
    supportedContentTypes: ['single_tweet'],
    postUrlTemplate: null,
    replyUrlTemplate: null,
    oauthSupported: false,
    requiresHandle: false,
    connectPermissions: [],
    enabled: false,
  },
  bluesky: {
    id: 'bluesky',
    displayName: 'Bluesky',
    charLimit: 300,
    supportsMedia: true,
    mediaRequired: false,
    supportedContentTypes: ['single_tweet', 'thread'],
    postUrlTemplate: 'https://bsky.app/profile/{user}/post/{id}',
    replyUrlTemplate: 'https://bsky.app/profile/{user}/post/{id}',
    oauthSupported: true,
    requiresHandle: true,
    connectPermissions: [
      'Read your profile and posts',
      'Post on your behalf',
    ],
    enabled: true,
  },
  truthsocial: {
    id: 'truthsocial',
    displayName: 'Truth Social',
    charLimit: 500,
    supportsMedia: true,
    mediaRequired: false,
    supportedContentTypes: ['single_tweet'],
    postUrlTemplate: null,
    replyUrlTemplate: null,
    oauthSupported: false,
    requiresHandle: false,
    connectPermissions: [],
    enabled: false,
  },
  linkedin: {
    id: 'linkedin',
    displayName: 'LinkedIn',
    charLimit: 3000,
    supportsMedia: true,
    mediaRequired: false,
    supportedContentTypes: ['single_tweet'],
    postUrlTemplate: null,
    replyUrlTemplate: null,
    oauthSupported: false,
    requiresHandle: false,
    connectPermissions: [],
    enabled: false,
  },
  facebook: {
    id: 'facebook',
    displayName: 'Facebook',
    charLimit: 63206,
    supportsMedia: true,
    mediaRequired: false,
    supportedContentTypes: ['single_tweet'],
    postUrlTemplate: null,
    replyUrlTemplate: null,
    oauthSupported: false,
    requiresHandle: false,
    connectPermissions: [],
    enabled: false,
  },
}

export function getPlatformConfig(platform: AccountPlatform): PlatformConfig {
  return PLATFORM_CONFIGS[platform] ?? PLATFORM_CONFIGS.twitter
}

export function getCharLimit(platform: AccountPlatform): number {
  return getPlatformConfig(platform).charLimit
}

export function getPostUrl(platform: AccountPlatform, id: string): string | null {
  const template = getPlatformConfig(platform).postUrlTemplate
  if (!template || !id) return null
  return template.replace('{id}', id)
}

export function getReplyUrl(platform: AccountPlatform, id: string): string | null {
  const template = getPlatformConfig(platform).replyUrlTemplate
  if (!template || !id) return null
  return template.replace('{id}', id)
}

export function getContentTypesForPlatform(platform: AccountPlatform): ContentType[] {
  return getPlatformConfig(platform).supportedContentTypes
}

export function getPlatformDisplayName(platform: AccountPlatform): string {
  return getPlatformConfig(platform).displayName
}

export function getItemPlatform(item: ContentQueueItem): AccountPlatform {
  return item.platform ?? 'twitter'
}

export function getPostedPlatformId(item: ContentQueueItem): string | undefined {
  return item.posted_platform_id ?? item.twitter_post_id
}

export function getReplyToPlatformId(item: ContentQueueItem): string | null | undefined {
  return item.reply_to_platform_id ?? item.reply_to_tweet_id
}

export function getItemPostUrl(item: ContentQueueItem): string | null {
  if (item.post_url) return item.post_url
  const postId = getPostedPlatformId(item)
  if (!postId) return null
  return getPostUrl(getItemPlatform(item), postId)
}
